// =============================================================================
// PEDRA — src/optimizer/cuts.ts
// Extracao da SEQUENCIA DE CORTES a partir da arvore guilhotinada, coleta de
// sobras e reconstrucao (validacao) de um layout qualquer como guilhotinado.
//
// Ordem entregue ao operador: estagio crescente -> profundidade do no na
// arvore -> coordenada do corte -> id do no. A serra sempre avanca no mesmo
// sentido dentro de cada estagio.
// =============================================================================

import type {
  CutAxis,
  CutChild,
  CutInstruction,
  FreeArea,
  Micron,
  Placement,
  Rect,
  Settings,
} from '../core/types';
import type { GuillotineNode, PackedSheet } from './guillotine';
import { cutStageOf } from './guillotine';
import { containsRect, intersectsRect, makeRect, rectArea, isUsableOffcut } from './geometry';
import { hash32 } from './rng';

/** Acima disto a reconstrucao guilhotinada fica cara demais e devolvemos null. */
const MAX_RECONSTRUCT_PLACEMENTS = 160;
/** Teto de chamadas recursivas da reconstrucao (protecao contra explosao). */
const MAX_RECONSTRUCT_CALLS = 8_000;
/** Teto de trabalho elementar da reconstrucao (protecao contra caso patologico). */
const MAX_RECONSTRUCT_WORK = 2_000_000;

// -----------------------------------------------------------------------------
// SEQUENCIA DE CORTES
// -----------------------------------------------------------------------------

/** Retangulo do filho A (lado de menor coordenada) de um no de corte. */
function childARect(node: GuillotineNode): Rect {
  const r = node.rect;
  return node.axis === 'x'
    ? makeRect(r.x, r.y, node.cutPos - r.x, r.length)
    : makeRect(r.x, r.y, r.width, node.cutPos - r.y);
}

/** Retangulo do filho B (lado de maior coordenada) de um no de corte. */
function childBRect(node: GuillotineNode): Rect {
  const r = node.rect;
  const start = node.cutPos + node.kerf;
  return node.axis === 'x'
    ? makeRect(start, r.y, r.x + r.width - start, r.length)
    : makeRect(r.x, start, r.width, r.y + r.length - start);
}

function toCutChild(rect: Rect, isWaste: boolean): CutChild {
  return { x: rect.x, y: rect.y, width: rect.width, length: rect.length, isWaste };
}

/**
 * Converte a arvore em instrucoes de corte, na ordem de execucao.
 * Percurso iterativo (nada de recursao) — a arvore de 2000 pecas nao pode
 * estourar a pilha.
 */
export function extractCuts(
  packed: PackedSheet,
  sheetIndex: number,
  _settings: Settings,
): CutInstruction[] {
  const nodes = packed.nodes;
  const n = nodes.length;
  if (n === 0) return [];

  // Profundidade: como todo filho e criado depois do pai, uma passada para
  // frente basta.
  const depth = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    const nd = nodes[i] as GuillotineNode;
    depth[i] = nd.parent >= 0 ? (depth[nd.parent] as number) + 1 : 0;
  }

  // Subarvore contem peca util? Uma passada para tras resolve.
  const hasUsed = new Uint8Array(n);
  for (let i = n - 1; i >= 0; i--) {
    const nd = nodes[i] as GuillotineNode;
    if (nd.kind === 'used') {
      hasUsed[i] = 1;
      continue;
    }
    let flag = 0;
    if (nd.childA >= 0 && hasUsed[nd.childA] === 1) flag = 1;
    if (flag === 0 && nd.childB >= 0 && hasUsed[nd.childB] === 1) flag = 1;
    hasUsed[i] = flag;
  }

  // O `stage` de uma INSTRUCAO e o estagio que o corte produz — igual ao dos
  // filhos, nao o do no que esta sendo dividido. O primeiro corte de uma chapa
  // (raiz, `lastAxis === null`) e sempre estagio 1.
  const splits: Array<{ node: GuillotineNode; stage: number }> = [];
  for (let i = 0; i < n; i++) {
    const nd = nodes[i] as GuillotineNode;
    if (nd.kind === 'split' && nd.axis !== null) {
      splits.push({ node: nd, stage: cutStageOf(nd, nd.axis) });
    }
  }

  splits.sort((a, b) => {
    if (a.stage !== b.stage) return a.stage - b.stage;
    const da = depth[a.node.id] as number;
    const db = depth[b.node.id] as number;
    if (da !== db) return da - db;
    if (a.node.cutPos !== b.node.cutPos) return a.node.cutPos - b.node.cutPos;
    return a.node.id - b.node.id;
  });

  const cuts: CutInstruction[] = [];
  for (let i = 0; i < splits.length; i++) {
    const entry = splits[i] as { node: GuillotineNode; stage: number };
    const nd = entry.node;
    const axis = nd.axis as CutAxis;
    const r = nd.rect;
    const start = axis === 'x' ? r.y : r.x;
    const end = axis === 'x' ? r.y + r.length : r.x + r.width;
    const aRect = childARect(nd);
    const bRect = childBRect(nd);
    const aWaste = nd.childA >= 0 ? hasUsed[nd.childA] === 0 : true;
    const bWaste = nd.childB >= 0 ? hasUsed[nd.childB] === 0 : true;

    cuts.push({
      seq: cuts.length + 1,
      sheetIndex,
      axis,
      pos: nd.cutPos,
      start,
      end,
      length: end - start,
      kerf: nd.kerf,
      stage: entry.stage,
      parent: { x: r.x, y: r.y, width: r.width, length: r.length },
      childA: toCutChild(aRect, aWaste),
      childB: toCutChild(bRect, bWaste),
    });
  }

  return cuts;
}

// -----------------------------------------------------------------------------
// SOBRAS
// -----------------------------------------------------------------------------

/**
 * Converte os retangulos livres em `FreeArea`, marcando quais sao
 * reaproveitaveis (`usable`) segundo `minTrim`.
 * Ordenadas da MAIOR para a menor area (a primeira e a "melhor sobra").
 */
export function collectOffcuts(
  freeRects: readonly Rect[],
  minTrim: Micron,
  sheetIndex: number,
): FreeArea[] {
  const rects = freeRects.filter((r) => r.width > 0 && r.length > 0);
  rects.sort((a, b) => {
    const da = b.width * b.length - a.width * a.length;
    if (da !== 0) return da > 0 ? 1 : -1;
    if (a.y !== b.y) return a.y - b.y;
    if (a.x !== b.x) return a.x - b.x;
    if (a.width !== b.width) return a.width - b.width;
    return a.length - b.length;
  });

  return rects.map((r, i) => ({
    id: `off-${sheetIndex}-${i}`,
    x: r.x,
    y: r.y,
    width: r.width,
    length: r.length,
    area: rectArea(r),
    usable: isUsableOffcut(r, minTrim),
  }));
}

// -----------------------------------------------------------------------------
// RECONSTRUCAO / VALIDACAO GUILHOTINADA
// -----------------------------------------------------------------------------

interface TmpNode {
  rect: Rect;
  kind: 'free' | 'used' | 'split';
  placement: number;
  axis: CutAxis | null;
  cutPos: Micron;
  kerf: Micron;
  a: TmpNode | null;
  b: TmpNode | null;
}

interface CutCandidate {
  axis: CutAxis;
  pos: Micron;
  /** 0 quando o corte separa pecas dos dois lados (preferido), 1 quando apara. */
  rank: number;
}

function freeTmp(rect: Rect): TmpNode {
  return { rect, kind: 'free', placement: -1, axis: null, cutPos: 0, kerf: 0, a: null, b: null };
}

function placementRect(p: Placement): Rect {
  return { x: p.x, y: p.y, width: p.width, length: p.length };
}

/**
 * Tenta descrever `placements` dentro de `region` como uma sequencia de cortes
 * passantes. Devolve a arena da arvore, ou `null` quando o layout NAO e
 * guilhotinavel (caso tipico de um resultado MaxRects em escada).
 */
export function reconstructGuillotine(
  region: Rect,
  placements: readonly Placement[],
  kerf: Micron,
): GuillotineNode[] | null {
  if (region.width <= 0 || region.length <= 0) {
    return placements.length === 0 ? flatten(freeTmp(region)) : null;
  }
  if (placements.length > MAX_RECONSTRUCT_PLACEMENTS) return null;

  const k = Number.isFinite(kerf) && kerf > 0 ? kerf : 0;

  // Pre-condicoes: tudo dentro da regiao e sem sobreposicao.
  for (let i = 0; i < placements.length; i++) {
    const pi = placements[i] as Placement;
    if (pi.width <= 0 || pi.length <= 0) return null;
    const ri = placementRect(pi);
    if (!containsRect(region, ri)) return null;
    for (let j = i + 1; j < placements.length; j++) {
      if (intersectsRect(ri, placementRect(placements[j] as Placement))) return null;
    }
  }

  const budget: Budget = { calls: 0, work: 0 };
  const all: number[] = [];
  for (let i = 0; i < placements.length; i++) all.push(i);

  const root = buildTmp(region, all, placements, k, budget);
  if (root === null) return null;
  return flatten(root);
}

interface Budget {
  calls: number;
  work: number;
}

function buildTmp(
  region: Rect,
  idxs: readonly number[],
  placements: readonly Placement[],
  kerf: Micron,
  budget: Budget,
): TmpNode | null {
  if (++budget.calls > MAX_RECONSTRUCT_CALLS) return null;
  if (budget.work > MAX_RECONSTRUCT_WORK) return null;

  if (idxs.length === 0) return freeTmp(region);

  if (idxs.length === 1) {
    const p = placements[idxs[0] as number] as Placement;
    if (
      p.x === region.x &&
      p.y === region.y &&
      p.width === region.width &&
      p.length === region.length
    ) {
      return {
        rect: region,
        kind: 'used',
        placement: idxs[0] as number,
        axis: null,
        cutPos: 0,
        kerf: 0,
        a: null,
        b: null,
      };
    }
  }

  for (const cand of candidateCuts(region, idxs, placements, kerf, budget)) {
    budget.work += idxs.length;
    if (budget.work > MAX_RECONSTRUCT_WORK) return null;
    const split = trySplit(region, idxs, placements, kerf, cand);
    if (split === null) continue;

    const a = buildTmp(split.regionA, split.idxsA, placements, kerf, budget);
    if (a === null) continue;
    const b = buildTmp(split.regionB, split.idxsB, placements, kerf, budget);
    if (b === null) continue;

    return {
      rect: region,
      kind: 'split',
      placement: -1,
      axis: cand.axis,
      cutPos: cand.pos,
      kerf: split.kerfUsed,
      a,
      b: split.regionB.width > 0 && split.regionB.length > 0 ? b : null,
    };
  }

  return null;
}

interface SplitTrial {
  regionA: Rect;
  regionB: Rect;
  idxsA: number[];
  idxsB: number[];
  kerfUsed: Micron;
}

function trySplit(
  region: Rect,
  idxs: readonly number[],
  placements: readonly Placement[],
  kerf: Micron,
  cand: CutCandidate,
): SplitTrial | null {
  const idxsA: number[] = [];
  const idxsB: number[] = [];
  const bandEnd = cand.pos + kerf;

  for (let i = 0; i < idxs.length; i++) {
    const p = placements[idxs[i] as number] as Placement;
    const near = cand.axis === 'x' ? p.x : p.y;
    const far = cand.axis === 'x' ? p.x + p.width : p.y + p.length;
    if (far <= cand.pos) idxsA.push(idxs[i] as number);
    else if (near >= bandEnd) idxsB.push(idxs[i] as number);
    else return null; // a peca atravessa a lamina: corte invalido
  }

  const regionA =
    cand.axis === 'x'
      ? makeRect(region.x, region.y, cand.pos - region.x, region.length)
      : makeRect(region.x, region.y, region.width, cand.pos - region.y);

  const regionB =
    cand.axis === 'x'
      ? makeRect(bandEnd, region.y, region.x + region.width - bandEnd, region.length)
      : makeRect(region.x, bandEnd, region.width, region.y + region.length - bandEnd);

  if (regionA.width <= 0 || regionA.length <= 0) return null;
  if (idxsB.length > 0 && (regionB.width <= 0 || regionB.length <= 0)) return null;

  // A lamina so e cobrada quando de fato sobra material do outro lado.
  const kerfUsed = cand.axis === 'x'
    ? region.x + region.width - cand.pos > 0
      ? kerf
      : 0
    : region.y + region.length - cand.pos > 0
      ? kerf
      : 0;

  return { regionA, regionB, idxsA, idxsB, kerfUsed };
}

/** Quantos elementos de `sorted` (crescente) sao <= `v`. Busca binaria. */
function countLessOrEqual(sorted: readonly number[], v: number): number {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((sorted[mid] as number) <= v) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Quantos elementos de `sorted` (crescente) sao < `v`. Busca binaria. */
function countLess(sorted: readonly number[], v: number): number {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((sorted[mid] as number) < v) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Posicoes de corte plausiveis: as bordas das pecas, dos dois lados da lamina.
 * O `rank` prioriza cortes que deixam pecas nos DOIS lados (progresso real)
 * sobre cortes de aparo. Custo O(n log n) — nada de varredura quadratica.
 */
function candidateCuts(
  region: Rect,
  idxs: readonly number[],
  placements: readonly Placement[],
  kerf: Micron,
  budget: Budget,
): CutCandidate[] {
  const out: CutCandidate[] = [];
  const n = idxs.length;
  budget.work += n * 4;

  for (const axis of ['x', 'y'] as const) {
    const lo = axis === 'x' ? region.x : region.y;
    const hi = axis === 'x' ? region.x + region.width : region.y + region.length;

    const nears: number[] = new Array<number>(n);
    const fars: number[] = new Array<number>(n);
    for (let i = 0; i < n; i++) {
      const p = placements[idxs[i] as number] as Placement;
      nears[i] = axis === 'x' ? p.x : p.y;
      fars[i] = axis === 'x' ? p.x + p.width : p.y + p.length;
    }
    const nearsSorted = nears.slice().sort((a, b) => a - b);
    const farsSorted = fars.slice().sort((a, b) => a - b);

    const positions: number[] = [];
    for (let i = 0; i < n; i++) {
      positions.push(fars[i] as number);
      positions.push((nears[i] as number) - kerf);
    }
    positions.sort((a, b) => a - b);

    let prev = Number.NaN;
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i] as number;
      if (pos === prev) continue;
      prev = pos;
      if (pos <= lo || pos >= hi) continue;
      const before = countLessOrEqual(farsSorted, pos);
      const after = n - countLess(nearsSorted, pos + kerf);
      out.push({ axis, pos, rank: before > 0 && after > 0 ? 0 : 1 });
    }
  }

  out.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    if (a.axis !== b.axis) return a.axis === 'x' ? -1 : 1;
    return a.pos - b.pos;
  });
  return out;
}

/** Achata a arvore temporaria na arena definitiva, calculando os estagios. */
function flatten(root: TmpNode): GuillotineNode[] {
  const nodes: GuillotineNode[] = [];
  const stack: Array<{ tmp: TmpNode; parent: number; stage: number; lastAxis: CutAxis | null }> = [
    { tmp: root, parent: -1, stage: 0, lastAxis: null },
  ];

  while (stack.length > 0) {
    const job = stack.pop() as {
      tmp: TmpNode;
      parent: number;
      stage: number;
      lastAxis: CutAxis | null;
    };
    const id = nodes.length;
    const node: GuillotineNode = {
      id,
      rect: job.tmp.rect,
      parent: job.parent,
      kind: job.tmp.kind,
      stage: job.stage,
      lastAxis: job.lastAxis,
      placement: job.tmp.placement,
      axis: job.tmp.axis,
      cutPos: job.tmp.cutPos,
      kerf: job.tmp.kerf,
      childA: -1,
      childB: -1,
    };
    nodes.push(node);

    if (job.tmp.kind !== 'split' || job.tmp.axis === null) continue;

    const childStage = cutStageOf(node, job.tmp.axis);
    // Empilha B antes de A para que A receba o menor id (ordem canonica).
    if (job.tmp.b !== null) {
      stack.push({ tmp: job.tmp.b, parent: id, stage: childStage, lastAxis: job.tmp.axis });
    }
    if (job.tmp.a !== null) {
      stack.push({ tmp: job.tmp.a, parent: id, stage: childStage, lastAxis: job.tmp.axis });
    }
    // Os ids reais sao resolvidos quando os filhos forem desempilhados: como o
    // topo da pilha e sempre processado primeiro e cada filho registra o pai,
    // basta religar depois (feito na passada abaixo).
  }

  // Religa childA/childB a partir de `parent` (A = primeiro filho criado).
  for (let i = 0; i < nodes.length; i++) {
    const nd = nodes[i] as GuillotineNode;
    if (nd.parent < 0) continue;
    const parent = nodes[nd.parent] as GuillotineNode;
    if (parent.childA === -1) parent.childA = nd.id;
    else if (parent.childB === -1) parent.childB = nd.id;
  }

  return nodes;
}

// -----------------------------------------------------------------------------
// ASSINATURA DE LAYOUT
// -----------------------------------------------------------------------------

/**
 * Assinatura estavel de um plano de corte. Duas chapas com o MESMO conjunto de
 * pecas nas MESMAS posicoes produzem a mesma string — e por isso podem ser
 * agrupadas num unico diagrama com `repeat > 1`.
 * O numero da copia (`instance`) e ignorado de proposito: copias sao
 * intercambiaveis.
 */
export function layoutHash(placements: readonly Placement[]): string {
  const keys: string[] = [];
  for (let i = 0; i < placements.length; i++) {
    const p = placements[i] as Placement;
    keys.push(`${p.partId}|${p.x}|${p.y}|${p.width}|${p.length}|${p.rotated ? 1 : 0}`);
  }
  keys.sort();
  const joined = keys.join(';');
  const h1 = hash32(joined).toString(36);
  const h2 = hash32(joined.length, 'pedra', joined).toString(36);
  return `${placements.length}.${h1}.${h2}`;
}
